<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">内容运营</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">内容</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">搜索热词管理</span></h2>
      <div class="header-actions">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索热词..."
          clearable
          prefix-icon="Search"
          style="width: 240px;"
          @input="onSearchInput"
        />
        <el-button type="primary" @click="showDialog(false)">新增热词</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab" @tab-change="fetchData">
      <el-tab-pane label="全部" name="" />
      <el-tab-pane v-for="t in tabs" :key="t.value" :label="t.label" :name="t.value" />
    </el-tabs>

    <el-table :data="list" stripe v-loading="loading">
      <el-table-column label="模块" width="120">
        <template #default="{row}">{{ moduleLabel(row.module) }}</template>
      </el-table-column>
      <el-table-column label="热词" prop="keyword" min-width="200" />
      <el-table-column label="排序" width="80">
        <template #default="{row}">
          <el-input-number v-model="row.sort_order" :min="0" size="small" controls-position="right" @change="handleFieldChange(row, 'sort_order')" />
        </template>
      </el-table-column>
      <el-table-column label="状态" width="80">
        <template #default="{row}">
          <el-switch v-model="row.status" @change="handleFieldChange(row, 'status')" />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160">
        <template #default="{row}">
          <el-button size="small" @click="showDialog(true, row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="pagination.total > 0"
      v-model:current-page="pagination.current_page"
      v-model:page-size="pagination.per_page"
      :total="pagination.total"
      layout="total, sizes, prev, pager, next, jumper"
      :page-sizes="[10,20,50]"
      small
      background
      style="margin-top:16px;justify-content:flex-end"
      @current-change="fetchData"
      @size-change="fetchData"
    />

    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑热词' : '新增热词'" width="500px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="所属模块" prop="module">
          <el-select v-model="form.module" style="width:100%">
            <el-option v-for="t in tabs" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="热词" prop="keyword">
          <el-input v-model="form.keyword" placeholder="请输入搜索热词" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as api from '../../api/contentManagement';

const tabs = [
  { value: 'training', label: '宠镜训练' },
  { value: 'mall', label: '商城' },
  { value: 'activity', label: '活动' },
];

const list = ref([]);
const loading = ref(false);
const pagination = reactive({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
const dialogVisible = ref(false);
const isEdit = ref(false);
const formRef = ref(null);
const activeTab = ref('');
const searchKeyword = ref('');

const form = ref({ module: 'training', keyword: '', sort_order: 0, status: true });
const rules = {
  module: [{ required: true, message: '请选择模块', trigger: 'change' }],
  keyword: [{ required: true, message: '请输入热词', trigger: 'blur' }],
};

let searchTimer = null;
const onSearchInput = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(fetchData, 300);
};

const moduleLabel = (m) => {
  const found = tabs.find(t => t.value === m);
  return found ? found.label : m;
};

const fetchData = async () => {
  loading.value = true;
  try {
    const params = { page: pagination.current_page, per_page: pagination.per_page };
    if (activeTab.value) params.module = activeTab.value;
    if (searchKeyword.value) params.search = searchKeyword.value;
    const r = await api.getSearchHotwords(params);
    list.value = r.data || [];
    pagination.current_page = r.pagination?.current_page || 1;
    pagination.total = r.pagination?.total || 0;
  } catch {
    ElMessage.error('获取列表失败');
  } finally {
    loading.value = false;
  }
};

const showDialog = (edit, row) => {
  isEdit.value = edit;
  if (row) {
    form.value = { ...row };
  } else {
    // 新增时预选当前 tab 的模块
    const defaultModule = activeTab.value || 'training';
    form.value = { module: defaultModule, keyword: '', sort_order: 0, status: true };
  }
  dialogVisible.value = true;
};

const handleFieldChange = (row, field) => {
  api.updateSearchHotword(row.id, { [field]: row[field] });
};

const handleSave = async () => {
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;
  try {
    if (isEdit.value) {
      await api.updateSearchHotword(form.value.id, form.value);
      ElMessage.success('更新成功');
    } else {
      await api.createSearchHotword(form.value);
      ElMessage.success('创建成功');
    }
    dialogVisible.value = false;
    await fetchData();
  } catch {
    ElMessage.error('操作失败');
  }
};

const handleDelete = (row) => {
  ElMessageBox.confirm('确定删除？', '提示').then(async () => {
    await api.deleteSearchHotword(row.id);
    ElMessage.success('删除成功');
    await fetchData();
  }).catch(() => {});
};

onMounted(fetchData);
</script>

<style scoped>
.header-actions { display: flex; gap: 12px; align-items: center; }
</style>
